import { TFunction } from 'i18next';

/**
 * Maps raw backend or Google API errors into clear, localized user-friendly messages.
 */
export function formatGoogleError(rawError: any, t: TFunction): string {
  if (!rawError) {
    return t('googleErrors.syncFailed', 'Failed to sync with Google Contacts. Please check server settings and permissions.');
  }

  const errStr = typeof rawError === 'string' ? rawError : (rawError?.message || rawError?.error || JSON.stringify(rawError));
  const errLower = errStr.toLowerCase();

  if (
    errLower.includes('502') ||
    errLower.includes('bad gateway') ||
    errLower.includes('gateway timeout') ||
    errLower.includes('504') ||
    errLower.includes('<!doctype') ||
    errLower.includes('<html')
  ) {
    return t(
      'googleErrors.serverTimeout',
      'The sync is taking longer than expected due to a large number of contacts. Synchronization is continuing in the background.'
    );
  }

  if (
    errLower.includes('unauthorized') ||
    errLower.includes('unauthenticated') ||
    errLower.includes('invalid authentication credentials') ||
    errLower.includes('invalid_token')
  ) {
    return t(
      'googleErrors.unauthorized',
      'Google authorization expired or invalid. Please sign in again.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_UNAUTHORIZED_CLIENT') ||
    errStr.includes('unauthorized_client') ||
    errStr.includes('not authorized for any of the scopes requested')
  ) {
    return t(
      'googleErrors.unauthorizedClient',
      "Google Workspace Domain-Wide Delegation is not authorized for Contacts. Please add scope 'https://www.googleapis.com/auth/contacts' in Google Workspace Admin Console."
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_CREDENTIALS_MISSING') ||
    errStr.includes('credentials missing') ||
    errStr.includes('credentials not configured')
  ) {
    return t(
      'googleErrors.credentialsMissing',
      'Google integration credentials are not configured. Please upload credentials in Company Settings.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_INVALID_CREDENTIALS') ||
    errStr.includes('Invalid Google credentials')
  ) {
    return t(
      'googleErrors.invalidCredentials',
      'Invalid Google credentials format. Please check the JSON key in Company Settings.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_SYNC_DISABLED') ||
    errStr.includes('sync is not enabled')
  ) {
    return t(
      'googleErrors.contactsSyncDisabled',
      'Google Contacts synchronization is disabled in Company Settings.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_NO_TARGET_EMAIL') ||
    errStr.includes('Target Google email is not configured')
  ) {
    return t(
      'googleErrors.noTargetEmail',
      'No target Google Workspace user email specified for synchronization.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_SCOPE_INSUFFICIENT') ||
    errStr.includes('ACCESS_TOKEN_SCOPE_INSUFFICIENT') ||
    errStr.includes('insufficient authentication scopes')
  ) {
    return t(
      'googleErrors.scopeInsufficient',
      'Google account access was granted without Contacts permission. Please check the Contacts permission box in the Google authorization window.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_PEOPLE_API_DISABLED') ||
    errStr.includes('SERVICE_DISABLED') ||
    errStr.includes('People API has not been used') ||
    errStr.includes('Calendar API has not been used')
  ) {
    return t(
      'googleErrors.apiDisabled',
      'Google People API or Calendar API is disabled in Google Cloud Console. Please enable it in your GCP project.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_USER_NOT_FOUND') ||
    errStr.includes('User not found') ||
    errStr.includes('invalid_grant')
  ) {
    return t(
      'googleErrors.userNotFound',
      'The specified Google Workspace user account was not found in the domain.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_NO_CONTACTS') ||
    errStr.includes('No contacts found')
  ) {
    return t('googleErrors.noContactsToSync', 'No CRM contacts found to sync.');
  }

  if (
    errStr.includes('ERR_GOOGLE_RATE_LIMIT') ||
    errStr.includes('rateLimitExceeded')
  ) {
    return t(
      'googleErrors.rateLimitExceeded',
      'Google API rate limit exceeded. Please try again later.'
    );
  }

  if (
    errStr.includes('ERR_GOOGLE_CALENDAR_NOT_CONNECTED') ||
    errStr.includes('calendar')
  ) {
    return t(
      'googleErrors.calendarNotConnected',
      'Google Calendar is not connected or enabled in Company Settings.'
    );
  }

  return errStr;
}
